import React from "react";
import styled from "styled-components";
import FloatingActionBar from "../../../../library/FloatingActionBar";
import ProgressCard from "../../../../library/ProgressCard";
import { StyledDestructiveBulkButton } from "../../../../library/BulkToolbarButtons";
import PermissionsGate from "../../../../../utils/permissionGate";
import { tokens } from "../../../../../public/tokens";

/*
 * ProgressCard has no fixed positioning of its own (it's meant to sit inline
 * wherever a caller places it) - this anchors it in the same bottom-center
 * "slot" FloatingActionBar itself uses, so the bar and its progress
 * replacement occupy the same visual position per the spec's "join or
 * replace the bar" requirement. Only used here, so it lives in this file
 * rather than a shared versionModules styles file.
 */
const StyledProgressAnchor = styled.div({
    position: "fixed",
    bottom: tokens.globals["spacing-xl"],
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(420px, calc(100vw - 32px))",
    zIndex: 1000,
});

/**
 * Floating bulk-action bar for the Version Modules table: "N Modules
 * selected" + Unlink Modules, replaced by a `ProgressCard` (full "Unlinking...
 * N of M Modules" + Cancel remaining) while a batch unlink is running -
 * unlike the sibling `linkedProgrammes/LinkedProgrammeBulkActions.js`, which
 * only shows a bare spinner pill during its own bulk actions.
 * @param {object} hook State/handlers from `useVersionModules`.
 * @param {React.Ref} [barRef] Ref forwarded to the underlying bar element, so callers can measure its rendered height.
 */
function VersionModulesBulkActions({ hook, barRef }) {
    if (hook.progress) {
        return (
            <StyledProgressAnchor>
                <ProgressCard
                    label={hook.progress.label}
                    current={hook.progress.current}
                    total={hook.progress.total}
                    unit="Modules"
                    onCancel={hook.cancelRemaining}
                />
            </StyledProgressAnchor>
        );
    }

    if (!hook.selectedIds.length) {
        return null;
    }

    return (
        <FloatingActionBar
            ref={barRef}
            summary={`${hook.selectedIds.length} Module${hook.selectedIds.length === 1 ? "" : "s"} selected`}
        >
            <PermissionsGate scopes={["pmdelete"]} errorProps={{ disabled: true, title: "Insufficient Permissions" }}>
                <StyledDestructiveBulkButton
                    type="button"
                    disabled={hook.isLocked}
                    title={hook.isLocked ? "This version's settings are locked" : undefined}
                    onClick={() => hook.startConfirmation(hook.selectedIds)}
                >
                    <i className="fal fa-unlink" aria-hidden="true" />
                    Unlink Modules
                </StyledDestructiveBulkButton>
            </PermissionsGate>
        </FloatingActionBar>
    );
}

export default VersionModulesBulkActions;
